/**
 * @fileoverview Shared discard-changes confirmation modal for the import editor package.
 * @module components
 *
 * Responsibilities:
 * - Render an in-app confirmation dialog when a user attempts to leave dirty editor state.
 * - Keep discard confirmation UI app-agnostic so shared import-editor flows avoid browser-native dialogs.
 *
 * Dependencies:
 * - react
 * - lucide-react
 *
 * Architecture:
 * - Shared package local UI primitive consumed by UnifiedImportWizard and Blade.
 * - Replaces browser-native confirm flows with explicit in-app confirmation.
 *
 * Notes:
 * - This component is intentionally package-local and not exported publicly yet.
 */
import { useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';

/**
 * @component ConfirmDiscardModal
 *
 * Purpose:
 * - Confirm that the user wants to discard unsaved changes before leaving the current editor surface.
 *
 * Props:
 * - open: whether the modal should render.
 * - title: modal heading text.
 * - description: body copy describing the discard action.
 * - confirmButtonLabel: label for the destructive confirmation action.
 * - isSubmitting: whether the confirm action is in progress.
 * - onConfirm: callback invoked when the user confirms discard.
 * - onClose: callback invoked when the user cancels the discard.
 *
 * Behavior:
 * - Closes on Escape.
 * - Prevents duplicate submission while processing.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Must not invoke browser-native confirmation APIs.
 *
 * Performance:
 * - Renders nothing while closed.
 */
export interface ConfirmDiscardModalProps {
  open: boolean;
  title?: string;
  description?: React.ReactNode;
  confirmButtonLabel?: string;
  isSubmitting?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

export default function ConfirmDiscardModal({
  open,
  title = 'Discard unsaved changes?',
  description = 'You have unsaved changes. If you leave now, your edits will be lost.',
  confirmButtonLabel = 'Discard Changes',
  isSubmitting = false,
  onConfirm,
  onClose,
}: ConfirmDiscardModalProps) {
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, isSubmitting, onClose]);

  if (!open) {
    return null;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }
    await onConfirm();
  };

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={isSubmitting ? undefined : onClose} />
      <div className="relative w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
          <h3 className="font-semibold text-amber-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="ml-auto text-amber-400 transition-colors hover:text-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close discard confirmation"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          <p className="text-sm text-gray-600">{description}</p>

          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs font-medium text-amber-800">
            Discarding changes cannot be undone.
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-md border px-3 py-2 text-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Keep Editing
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-md bg-amber-600 px-3 py-2 text-sm text-white transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Discarding...' : confirmButtonLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
